import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Stack,
  Chip,
  Paper,
  Grid,
  Avatar,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  VideoLibrary as VideoIcon,
  Person as PersonIcon,
  CheckCircle as CheckIcon,
  PlayArrow as PlayIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import moment from "moment";

import MightyDataGrid from "../../../../../components/organisms/MightyDataGrid";
import StandardButton from "../../../../../components/StandardButton";
import {
  StyledVideosCard,
  VideosHeader,
  VideosContent,
} from "./StyledComponents";

export interface ProcessedVideo {
  id: string;
  url: string;
  clientId: string;
  clientName?: string;
  createdOn: any; // Firebase Timestamp or Date
  processed: boolean;
  processedOn?: any; // Firebase Timestamp or Date
  processedBy?: {
    firstName: string;
    lastName: string;
    uid: string;
  };
  feedback?: string;
  metadata?: {
    notes?: string;
    exercise?: {
      id: string;
      reportId: string;
    };
  };
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalVideos: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

interface ProcessedVideosTableProps {
  videos: ProcessedVideo[];
  loading?: boolean;
  pagination?: PaginationInfo;
  onVideoPlay?: (video: ProcessedVideo) => void;
  onRefresh?: () => void;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSize?: number;
}

type SortField = "created_on" | "processed_on" | "client_name";
type SortDirection = "asc" | "desc";

const ProcessedVideosTable: React.FC<ProcessedVideosTableProps> = ({
  videos,
  loading = false,
  pagination,
  onVideoPlay,
  onRefresh,
  onPageChange,
  onPageSizeChange,
  pageSize = 25,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<SortField>("created_on");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  // Debug logging
  console.log("🔍 ProcessedVideosTable Debug:", {
    videosReceived: videos?.length || 0,
    loading,
    pagination,
    sampleVideo: videos?.[0],
    sampleVideoDateTypes: videos?.[0]
      ? {
          createdOnType: typeof videos[0].createdOn,
          createdOnValue: videos[0].createdOn,
          processedOnType: typeof videos[0].processedOn,
          processedOnValue: videos[0].processedOn,
        }
      : null,
  });

  // Handle page changes for server-side pagination
  const handlePageChange = useCallback(
    (newPage: number) => {
      console.log("🔍 Page change requested:", {
        newPage,
        willCall: newPage + 1,
      });
      if (onPageChange) {
        // MightyDataGrid uses 0-based pages, our API uses 1-based
        onPageChange(newPage + 1);
      }
    },
    [onPageChange],
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      console.log("🔍 Page size change requested:", newPageSize);
      if (onPageSizeChange) {
        onPageSizeChange(newPageSize);
      }
    },
    [onPageSizeChange],
  );

  // Filter videos by search term (client-side filtering for current page)
  const filteredVideos = useMemo(() => {
    if (!searchTerm.trim()) {
      console.log(
        "🔍 No search term, returning all videos:",
        videos?.length || 0,
      );
      return videos;
    }

    const searchLower = searchTerm.toLowerCase();
    const filtered = videos.filter((video) =>
      video.clientName?.toLowerCase().includes(searchLower),
    );
    console.log("🔍 Filtered videos:", {
      searchTerm,
      originalCount: videos?.length || 0,
      filteredCount: filtered.length,
    });
    return filtered;
  }, [videos, searchTerm]);

  // DataGrid columns
  const columns = useMemo(
    () => [
      {
        field: "video",
        headerName: "Video",
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params: any) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar
              src={`${params.row.url}#t=1`}
              sx={{ width: 40, height: 40 }}
            >
              <VideoIcon />
            </Avatar>
            {onVideoPlay && (
              <IconButton
                size="small"
                onClick={() => onVideoPlay(params.row)}
                sx={{ color: "primary.main" }}
              >
                <PlayIcon />
              </IconButton>
            )}
          </Box>
        ),
      },
      {
        field: "clientName",
        headerName: "Client",
        flex: 1,
        minWidth: 150,
        renderCell: (params: any) => (
          <Stack direction="row" alignItems="center" spacing={1}>
            <PersonIcon color="primary" fontSize="small" />
            <Typography variant="body2">
              {params.row.clientName || "Unknown Client"}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "createdOn",
        headerName: "Created",
        width: 120,
        renderCell: (params: any) => {
          const date = params.row.createdOn;
          let parsedDate = null;

          if (date) {
            if (date.toDate) {
              // Firebase Timestamp
              parsedDate = date.toDate();
            } else if (typeof date === "string") {
              // ISO string from API
              parsedDate = new Date(date);
            } else if (date instanceof Date) {
              // Already a Date object
              parsedDate = date;
            }
          }

          return (
            <Typography variant="body2">
              {parsedDate && !isNaN(parsedDate.getTime())
                ? moment(parsedDate).format("MMM D, YYYY")
                : "-"}
            </Typography>
          );
        },
      },
      {
        field: "processedOn",
        headerName: "Processed",
        width: 120,
        renderCell: (params: any) => {
          const date = params.row.processedOn;
          let parsedDate = null;

          if (date) {
            if (date.toDate) {
              // Firebase Timestamp
              parsedDate = date.toDate();
            } else if (typeof date === "string") {
              // ISO string from API
              parsedDate = new Date(date);
            } else if (date instanceof Date) {
              // Already a Date object
              parsedDate = date;
            }
          }

          return (
            <Typography variant="body2">
              {parsedDate && !isNaN(parsedDate.getTime())
                ? moment(parsedDate).format("MMM D, YYYY")
                : "-"}
            </Typography>
          );
        },
      },
      {
        field: "processedBy",
        headerName: "Coach",
        width: 150,
        renderCell: (params: any) => (
          <Typography variant="body2">
            {params.row.processedBy
              ? `${params.row.processedBy.firstName} ${params.row.processedBy.lastName}`
              : "-"}
          </Typography>
        ),
      },
      {
        field: "metadata",
        headerName: "Exercise",
        flex: 1,
        minWidth: 200,
        renderCell: (params: any) => (
          <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
            {params.row.metadata?.notes || "No exercise info"}
          </Typography>
        ),
      },
      {
        field: "feedback",
        headerName: "Feedback",
        flex: 1,
        minWidth: 200,
        renderCell: (params: any) => (
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.75rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {params.row.feedback
              ? params.row.feedback.length > 50
                ? `${params.row.feedback.substring(0, 50)}...`
                : params.row.feedback
              : "No feedback"}
          </Typography>
        ),
      },
    ],
    [onVideoPlay],
  );

  // Prepare rows for MightyDataGrid
  const gridRows = useMemo(() => {
    const rows = filteredVideos.map((video, index) => ({
      ...video,
      id: video.id || (video as any).uid || `video-${index}`,
    }));
    console.log("🔍 Grid rows prepared:", {
      count: rows.length,
      sampleRow: rows[0]
        ? { id: rows[0].id, clientName: rows[0].clientName }
        : null,
    });
    return rows;
  }, [filteredVideos]);

  console.log("🔍 MightyDataGrid props:", {
    rowsCount: gridRows.length,
    totalRows: pagination?.totalVideos || 0,
    pageSize,
    currentPage: (pagination?.currentPage || 1) - 1,
    loading,
  });

  return (
    <StyledVideosCard>
      <VideosHeader>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <CheckIcon color="success" sx={{ fontSize: 28 }} />
          <Typography variant="h6" fontWeight="bold">
            Previous Shared Videos
          </Typography>
          {pagination && (
            <Chip
              label={`${pagination.totalVideos} total videos`}
              color="success"
              size="small"
            />
          )}
        </Stack>
      </VideosHeader>

      <VideosContent>
        {/* Search and Sort Controls */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by client name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                  endAdornment: searchTerm && (
                    <IconButton size="small" onClick={() => setSearchTerm("")}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort by</InputLabel>
                <Select
                  value={sortField}
                  label="Sort by"
                  onChange={(e) => setSortField(e.target.value as SortField)}
                >
                  <MenuItem value="created_on">Created Date</MenuItem>
                  <MenuItem value="processed_on">Processed Date</MenuItem>
                  <MenuItem value="client_name">Client Name</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Order</InputLabel>
                <Select
                  value={sortDirection}
                  label="Order"
                  onChange={(e) =>
                    setSortDirection(e.target.value as SortDirection)
                  }
                >
                  <MenuItem value="desc">Newest First</MenuItem>
                  <MenuItem value="asc">Oldest First</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {onRefresh && (
              <Grid item xs={12} sm={12} md={4}>
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <StandardButton
                    onClick={onRefresh}
                    disabled={loading}
                    variant="primary"
                  >
                    Refresh
                  </StandardButton>
                </Box>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Data Grid with Server-side Pagination */}
        <MightyDataGrid
          entityName="processedVideos"
          rows={gridRows}
          columns={columns}
          loading={loading}
          onRowClick={onVideoPlay}
          showActions={false}
          actions={[]}
          paginationMode="server"
          totalRows={pagination?.totalVideos || 0}
          pageSize={pageSize}
          page={(pagination?.currentPage || 1) - 1}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          containerSx={{
            height: "calc(100vh - 400px)",
            minHeight: 400,
          }}
          getRowHeight={() => 60}
        />
      </VideosContent>
    </StyledVideosCard>
  );
};

export default ProcessedVideosTable;
